import React, { useEffect, useMemo, useState } from "react";
import Breadcrumbs from "../../Breadcrumbs";
import InsiteModal from "../insights/InsiteModal";
import {
  fetchDiscoverData,
  fetchDiscoverLanguage,
  fetchDiscoverLocation,
} from "../../features/discover/discoverSlice";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import { convertToNumber } from "../../utils/amountFormat";
import { cleanObject } from "../../utils/dateUtils";

const parseNumberValue = (num) => {
  if (!num) return 0;
  if (num >= 1000000) return (num / 1000000).toFixed(2) + "M";
  if (num >= 1000) return (num / 1000).toFixed(2) + "K";
  return num;
};

const contactOptions = [
  { label: "Email", value: "email" },
  { label: "Phone", value: "phone" }, // 🔥 map phone → whatsapp
  { label: "Instagram", value: "instagram" },
  { label: "Facebook", value: "facebook" },
  { label: "Twitter", value: "twitter" },
  { label: "YouTube", value: "youtube" },
  { label: "TikTok", value: "tiktok" },
  { label: "Snapchat", value: "snapchat" },
  { label: "Telegram", value: "telegram" },
  { label: "Whatsapp", value: "whatsapp" },
  { label: "Linktree", value: "linktree" },
  { label: "Threads ", value: "threads " },
];

const InfluencerDiscover = () => {
  const dispatch = useDispatch();
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(15);
  const [currentPage, setCurrentPage] = useState(1);
  const [showContacts, setShowContacts] = useState(false);

  const [showRemarksModal, setShowRemarksModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isSearched, setIsSearched] = useState(false);
  const { discoverDetails, status, error } = useSelector(
    (state) => state.discoverData,
  );

  const [toastDisplayed, setToastDisplayed] = useState(false);

  const [filteredData, setFilteredData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [channelName, setChannelName] = useState("instagram");

  const [formData, setFormData] = useState({
    audience_source: "any",
    paging: {
      limit: 15,
      skip: 0,
    },
    sort: {
      field: "followers",
      id: null,
      direction: "desc",
    },
    filter: {
      geo: [],
      audience_geo: [],
      audience_lang: [],
      lang: [],
      audience_gender: {
        code: "",
        weight: "",
      },
      gender: {
        code: "",
      },
      age: {
        left_number: "",
        right_number: "",
      },
      audience_age: [],
      text: "",
      followers: {
        left_number: "",
        right_number: "",
        type: "",
      },
      engagements: {
        left_number: "",
        right_number: "",
        type: "",
      },
      reel_views: {
        left_number: "",
        right_number: "",
        type: "",
      },
      reels_plays: {
        left_number: "",
        right_number: "",
        type: "",
      },
      views: {
        left_number: "",
        right_number: "",
        type: "",
      },
      account_type: [],
      posts_count: {
        left_number: "",
        right_number: "",
      },
      with_contact: [],
      last_posted: "",
      audience_credibility_class: [],
      audience_credibility: "",
      post_type:""
    },
  });

  const filteredRows = useMemo(() => {
    return filteredData.filter(
      (row) =>
        row.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row.name?.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [searchTerm, filteredData]);

  const toatalItemRecord = discoverDetails?.data?.result?.total;
  const totalItems = toatalItemRecord || 0;
  const totalPages = Math.ceil(totalItems / entriesPerPage);
  const start = (currentPage - 1) * entriesPerPage + 1;
  const end = Math.min(currentPage * entriesPerPage, totalItems);
  const currentRows = filteredData; //filteredRows.slice(indexOfFirstItem, indexOfLastItem);

  /* ---------- TABLE CHECKBOX ---------- */

  const handleRowSelect = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const handleSelectAll = (checked) => {
    setSelectedRows(checked ? currentRows.map((r) => r.id) : []);
  };

  /* ---------- SEARCH ---------- */

  const handleView = (row) => {
    setSelectedRow(row);
    setShowRemarksModal(true);
  };

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      paging: {
        ...prev.paging,
        limit: entriesPerPage,
        skip: (currentPage - 1) * entriesPerPage,
      },
    }));
  }, [entriesPerPage, currentPage]);

  const arrayFields = [
    "filter.geo",
    "filter.audience_geo",
    "filter.audience_age",
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const keys = name.split(".");

    setFormData((prev) => {
      const updated = { ...prev };
      let current = updated;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }

      const lastKey = keys[keys.length - 1];

      // ✅ HANDLE ARRAY FIELDS
      if (arrayFields.includes(name)) {
        current[lastKey] = value ? [value] : [];
      } else if (type === "checkbox" && Array.isArray(current[lastKey])) {
        // ✅ ONLY for account_type convert to number
        if (name === "filter.account_type") {
          const numericValue = Number(value);

          current[lastKey] = checked
            ? [...current[lastKey], numericValue]
            : current[lastKey].filter((v) => v !== numericValue);
        }
        // ✅ other checkboxes remain same
        else {
          current[lastKey] = checked
            ? [...current[lastKey], value]
            : current[lastKey].filter((v) => v !== value);
        }
      }

      // ✅ NUMBER
      else if (name.includes("left_number") || name.includes("right_number")) {
        // 🎯 SPECIAL CASES
        if (name.includes("filter.age")) {
          current[lastKey] = String(value); // ✅ age → string
        } else if (name.includes("filter.posts_count")) {
          current[lastKey] = value ? Number(value) : ""; // ✅ posts_count → number
        } else {
          current[lastKey] = value; // default (string)
        }
      }
      // ✅ NORMAL
      else {
        current[lastKey] = value;
      }

      return updated;
    });
  };

  console.log("form data ==>", formData);

  const handleContactSelect = (contact) => {
    setFormData((prev) => {
      const exists = (prev.filter.with_contact || []).includes(contact.value);

      return {
        ...prev,
        filter: {
          ...prev.filter,
          with_contact: exists
            ? prev.filter.with_contact.filter((c) => c !== contact.value)
            : [...(prev.filter.with_contact || []), contact.value],
        },
      };
    });
  };

  const emptyObject = (obj) => {
    return Object.fromEntries(
      Object.keys(obj).map((key) => {
        if (Array.isArray(obj[key])) return [key, []];
        if (typeof obj[key] === "object" && obj[key] !== null)
          return [key, emptyObject(obj[key])];
        return [key, ""];
      }),
    );
  };

  const handleClearFilter = () => {
  setFormData((prev) => ({
    ...prev,
    filter: emptyObject(prev.filter), // ✅ reset all fields
  }));

  // ✅ reset other related states
  setLocationInput({
    geo: "",
    audience_geo: "",
  });

  setLanguageInput({
    lang: "",
    audience_lang: "",
  });

  setLocationSuggestions({
    geo: [],
    audience_geo: [],
  });

  setLanguageSuggestions({
    lang: [],
    audience_lang: [],
  });

  setSelectedRows([]);
  setSearchTerm("");
  setFilteredData([]);
  setCurrentPage(1);
};

  // 🎯 Platform change
  // const handlePlatformChange = (platform) => {
  //   setChannelName(platform);
  //   setFormData((prev) => ({
  //     ...prev,
  //     filter: emptyObject(prev.filter), // ✅ reset structure but keep keys
  //   }));
  // };

  const handlePlatformChange = (platform) => {
    setChannelName(platform);

    setFormData((prev) => ({
      ...prev,
      filter: emptyObject(prev.filter),
    }));

    setLocationInput({
      geo: "",
      audience_geo: "",
    });

    setLanguageInput({
      lang: "",
      audience_lang: "",
    });

    setLocationSuggestions({
      geo: [],
      audience_geo: [],
    });

    setLanguageSuggestions({
      lang: [],
      audience_lang: [],
    });

    setSelectedRows([]);
    setSearchTerm("");
    setFilteredData([]);
  };

  // location search by api

  const [locationInput, setLocationInput] = useState({
    geo: "",
    audience_geo: "",
  });

  const [locationSuggestions, setLocationSuggestions] = useState({
    geo: [],
    audience_geo: [],
  });

  const handleLocationSearch = (value, type) => {
    // type = "geo" OR "audience_geo"

    // store typing value
    setLocationInput((prev) => ({
      ...prev,
      [type]: value,
    }));

    if (value.length < 3) {
      setLocationSuggestions((prev) => ({
        ...prev,
        [type]: [],
      }));
      return;
    }

    const payload = {
      query: value, // ✅ CORRECT (typed value)
      limit: 20,
    };

    dispatch(fetchDiscoverLocation({ payload: payload, channelName }))
      .unwrap()
      .then((res) => {
        setLocationSuggestions((prev) => ({
          ...prev,
          [type]: res?.data || [],
        }));
      })
      .catch(() => {
        setLocationSuggestions((prev) => ({
          ...prev,
          [type]: [],
        }));
      });
  };

  const handleAddLocation = (location, type) => {
    // location = { id, name }

    setFormData((prev) => {
      const existing = prev.filter[type] || [];

      // ❌ prevent duplicate by id
      if (existing.some((item) => item.id === location.id)) return prev;

      return {
        ...prev,
        filter: {
          ...prev.filter,
          [type]: [...existing, location], // ✅ store object
        },
      };
    });

    // clear input
    setLocationInput((prev) => ({
      ...prev,
      [type]: "",
    }));

    setLocationSuggestions((prev) => ({
      ...prev,
      [type]: [],
    }));
  };

  const handleRemoveLocation = (id, type) => {
    setFormData((prev) => ({
      ...prev,
      filter: {
        ...prev.filter,
        [type]: prev.filter[type].filter((item) => item.id !== id),
      },
    }));
  };

  // end api search of location
  // for search language on handle change
  const [languageInput, setLanguageInput] = useState({
    lang: "",
    audience_lang: "",
  });

  const [languageSuggestions, setLanguageSuggestions] = useState({
    lang: [],
    audience_lang: [],
  });

  const handleLanguageSearch = (value, type) => {
    setLanguageInput((prev) => ({
      ...prev,
      [type]: value,
    }));

    if (value.length < 3) {
      setLanguageSuggestions((prev) => ({
        ...prev,
        [type]: [],
      }));
      return;
    }

    const payload = {
      query: value,
      limit: 20,
    };
    // if{channelName === "youtube"}{}
    dispatch(fetchDiscoverLanguage({ payload: payload, channelName }))
      .unwrap()
      .then((res) => {
        console.log("res data ==>", res);

        const normalized = res.data.result.languages.map((item) => ({
          id: item.id || item.code,
          name: item.name,
        }));

        setLanguageSuggestions((prev) => ({
          ...prev,
          [type]: normalized,
        }));
      })
      .catch(() => {
        setLanguageSuggestions((prev) => ({
          ...prev,
          [type]: [],
        }));
      });
  };
  console.log("language suggestion ==>", languageSuggestions);

  const handleAddLanguage = (language, type) => {
    setFormData((prev) => {
      const existing = prev.filter[type] || [];

      // prevent duplicate
      if (existing.some((item) => item.id === language.id)) return prev;

      return {
        ...prev,
        filter: {
          ...prev.filter,
          [type]: [...existing, language],
        },
      };
    });

    setLanguageInput((prev) => ({
      ...prev,
      [type]: "",
    }));

    setLanguageSuggestions((prev) => ({
      ...prev,
      [type]: [],
    }));
  };

  const handleRemoveLanguage = (id, type) => {
    setFormData((prev) => ({
      ...prev,
      filter: {
        ...prev.filter,
        [type]: prev.filter[type].filter((item) => item.id !== id),
      },
    }));
  };
  // end for search language

  const preparePayload = () => {
    /* ---------- ARRAY HELPER ---------- */
    const toArray = (val) => {
      if (!val) return [];
      return Array.isArray(val) ? val : [val];
    };
    // let payload = { ...formData };
    let payload = JSON.parse(JSON.stringify(formData));
    if (payload.filter.with_contact?.length > 0) {
      const contectArray = toArray(payload.filter.with_contact);
      payload.filter.with_contact = contectArray.map((type) => ({
        type,
        action: "must",
      }));
    }
    /* ---------- FIX ARRAY FIELDS ---------- */
    payload.filter.geo = toArray(payload.filter.geo);
    if (payload.filter.geo?.length > 0) {
      const geoArray = toArray(payload.filter.geo);
      payload.filter.geo = geoArray.map((item) => ({
        id: item.id,
      }));
    }
    payload.filter.audience_geo = toArray(payload.filter.audience_geo);
    if (payload.filter.audience_geo?.length > 0) {
      const audienceGeoArray = toArray(payload.filter.audience_geo);
      payload.filter.audience_geo = audienceGeoArray.map((item) => ({
        id: item.id,
      }));
    }
    if (payload.filter.lang?.length > 0) {
      payload.filter.lang = {
        code: payload.filter.lang[0].id, // ✅ single object
      };
    }

    if (payload.filter.audience_lang?.length > 0) {
      payload.filter.audience_lang = {
        code: payload.filter.audience_lang[0].id,
      };
    }

    /* ---------- FIX audience_age (MAIN FIX) ---------- */
    if (payload.filter.audience_age) {
      const ageArray = toArray(payload.filter.audience_age);

      payload.filter.audience_age = ageArray.map((age) => ({
        code: age,
      }));
    }

    if (formData.filter.age) {
      const { left_number, right_number } = formData.filter.age;

      if (left_number && right_number) {
        payload.filter.age = {
          left_number: Number(left_number),
          right_number: Number(right_number),
        };
      }
    }

    if (formData.filter.engagements) {
      const { left_number, right_number } = formData.filter.engagements;

      if (left_number && right_number) {
        payload.filter.engagements = {
          left_number: Number(left_number),
          right_number: Number(right_number),
        };
      }
    }
    if (formData.filter.reels_plays) {
      const { left_number, right_number } = formData.filter.reels_plays;

      if (left_number && right_number) {
        payload.filter.reels_plays = {
          left_number: Number(left_number),
          right_number: Number(right_number),
        };
      }
    }
    if (formData.filter.reel_views) {
      const { left_number, right_number } = formData.filter.reel_views;

      if (left_number && right_number) {
        payload.filter.reel_views = {
          left_number: Number(left_number),
          right_number: Number(right_number),
        };
      }
    }
    if (formData.filter.followers) {
      const { left_number, right_number } = formData.filter.followers;

      if (left_number && right_number) {
        if (channelName === "youtube") {
          payload.filter.followers = {
            left_number: Number(left_number),
            right_number: Number(right_number),
            type: "",
          };
        } else {
          payload.filter.followers = {
            left_number: Number(left_number),
            right_number: Number(right_number),
          };
        }
      }
    }

    if (formData.filter.last_posted) {
      payload.filter.last_posted = Number(formData.filter.last_posted);
    }

    if (formData.filter.posts_count) {
      const { left_number, right_number } = formData.filter.posts_count;

      if (left_number && right_number) {
        payload.filter.posts_count = {
          left_number: Number(left_number),
          right_number: Number(right_number),
        };
      }
    }

    if (formData.filter.audience_credibility) {
      payload.filter.audience_credibility = convertToNumber(
        formData.filter.audience_credibility,
      );
    }

    const cleanedFilter = cleanObject(payload.filter || {});
    // payload.filter = Object.keys(cleanedFilter).length ? cleanedFilter : {};
    payload.filter =
      cleanedFilter && Object.keys(cleanedFilter).length > 0
        ? cleanedFilter
        : {};

    return payload;
  };

  const handleSearch = async () => {
    try {
      setIsSearched(true);

      const payload = preparePayload();

      // ✅ ALWAYS calculate fresh skip here
      const skip = (currentPage - 1) * entriesPerPage;

      payload.paging = {
        limit: entriesPerPage,
        skip: skip,
      };

      console.log("FINAL PAYLOAD =>", payload);

      const res = await dispatch(
        fetchDiscoverData({
          payload,
          channelName,
        }),
      ).unwrap();

      console.log("SUCCESS", res);
    } catch (err) {
      console.log("ERROR", err);
    }
  };

  useEffect(() => {
    if (isSearched) {
      handleSearch();
    }
  }, [currentPage, entriesPerPage]);

  useEffect(() => {
    if (discoverDetails?.data?.result?.lookalikes) {
      const apiData = discoverDetails.data.result.lookalikes;

      const formatted = apiData.map((item) => ({
        id: item.userId,
        username: item.profile.username,
        name: item.profile.fullname,
        followers: item.profile.followers,
        engagement: item.profile.engagementRate,
        engagements: item.profile.engagements,
        verified: item.profile.isVerified,
        private: item.profile.isPrivate,
        image: item.profile.picture,
        url: item.profile.url,
      }));

      setFilteredData(formatted);
    }
  }, [discoverDetails]);

  const isVideoPlatform = channelName === "youtube" || channelName === "tiktok";

  const fieldName = isVideoPlatform ? "filter.views" : "filter.reels_plays";

  const title = isVideoPlatform ? "Reels Views" : "Reels Plays";

  useEffect(() => {
    console.log("currentRows ==>", filteredData);
  }, [filteredData, locationSuggestions]);
  return (
    <div className="row">
      <div className="col-12">
        <div className="page-title-box d-sm-flex align-items-center justify-content-between">
          <h4 className="mb-sm-0">Influencers Discover</h4>

          <div className="page-title-right">
            <Breadcrumbs />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-12">
          <div className="row align-items-center py-2">
            <div className="col-lg-6"></div>

            <div className="col-lg-6 align-items-end gap-2">
              <div className="input-group d-flex justify-content-end">
                <div className=" shadow-sm rounded mb-2  dropdown">
                  <button
                    className="btn btn-light dropdown-toggle d-flex align-items-center gap-2 px-4 h-100"
                    type="button"
                    data-bs-toggle="dropdown"
                  >
                    <img
                      src={
                        channelName === "instagram"
                          ? "https://upload.wikimedia.org/wikipedia/commons/a/a5/Instagram_icon.png"
                          : channelName === "youtube"
                            ? "https://upload.wikimedia.org/wikipedia/commons/0/09/YouTube_full-color_icon_%282017%29.svg"
                            : channelName === "tiktok"
                              ? "https://upload.wikimedia.org/wikipedia/commons/a/a6/Tiktok_icon.svg"
                              : "https://upload.wikimedia.org/wikipedia/commons/c/ca/LinkedIn_logo_initials.png"
                      }
                      width="20"
                    />
                    {channelName.charAt(0).toUpperCase() + channelName.slice(1)}
                  </button>

                  <ul className="dropdown-menu dropdown-menu-end shadow">
                    <li>
                      <button
                        className="dropdown-item d-flex align-items-center gap-2"
                        onClick={() => handlePlatformChange("instagram")}
                      >
                        <img
                          src="https://upload.wikimedia.org/wikipedia/commons/a/a5/Instagram_icon.png"
                          width="20"
                        />
                        Instagram
                      </button>
                    </li>

                    <li>
                      <button
                        className="dropdown-item d-flex align-items-center gap-2"
                        onClick={() => handlePlatformChange("youtube")}
                      >
                        <img
                          src="https://upload.wikimedia.org/wikipedia/commons/0/09/YouTube_full-color_icon_%282017%29.svg"
                          width="22"
                        />
                        Youtube
                      </button>
                    </li>

                    <li>
                      <button
                        className="dropdown-item d-flex align-items-center gap-2"
                        onClick={() => handlePlatformChange("tiktok")}
                      >
                        <img
                          src="https://upload.wikimedia.org/wikipedia/commons/a/a6/Tiktok_icon.svg"
                          width="20"
                        />
                        TikTok
                      </button>
                    </li>

                    <li>
                      <button
                        className="dropdown-item d-flex align-items-center gap-2"
                        onClick={() => handlePlatformChange("linkedin")}
                      >
                        <img
                          src="https://upload.wikimedia.org/wikipedia/commons/c/ca/LinkedIn_logo_initials.png"
                          width="20"
                        />
                        LinkedIn
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <div className="card" id="leadsList">
            <div className="row card-header border-0">
              <div className="col-sm-3 col-md-3 col-lg-3 col-6 dropdown">
                <button
                  className="btn filter-btn d-flex align-items-center gap-2"
                  data-bs-toggle="dropdown"
                  data-bs-auto-close="outside"
                  aria-expanded="false"
                >
                  <i className="ri-flag-fill"></i>
                  Location
                  <i className="ri-arrow-down-s-line"></i>
                </button>

                <div className="dropdown-menu p-4 shadow filter-panel">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label d-flex align-items-center gap-2">
                        <i className="ri-group-line"></i> Audience
                        <i className="ri-information-fill text-muted"></i>
                      </label>

                      <input
                        className="form-control"
                        placeholder="Search location"
                        value={locationInput.audience_geo}
                        onChange={(e) =>
                          handleLocationSearch(e.target.value, "audience_geo")
                        }
                      />

                      {formData?.filter?.audience_geo?.map((loc, index) => (
                        <span key={index} className="badge bg-primary me-2">
                          {loc.name}
                          <span
                            style={{ cursor: "pointer", marginLeft: "5px" }}
                            onClick={() =>
                              handleRemoveLocation(loc.id, "audience_geo")
                            }
                          >
                            ❌
                          </span>
                        </span>
                      ))}
                    </div>

                    <div className="col-md-6">
                      <label className="form-label d-flex align-items-center gap-2">
                        <i className="ri-megaphone-line"></i> Influencer
                        <i className="ri-information-fill text-muted"></i>
                      </label>

                      <input
                        className="form-control"
                        placeholder="Search location"
                        value={locationInput.geo}
                        onChange={(e) =>
                          handleLocationSearch(e.target.value, "geo")
                        }
                      />

                      {formData.filter.geo.map((loc, index) => (
                        <span key={index} className="badge bg-success me-2">
                          {loc.name}
                          <span
                            style={{ cursor: "pointer", marginLeft: "5px" }}
                            onClick={() => handleRemoveLocation(loc.id, "geo")}
                          >
                            ❌
                          </span>
                        </span>
                      ))}
                    </div>
                    {locationSuggestions?.audience_geo?.result?.locations
                      ?.length > 0 && (
                      <div className="border mt-1 bg-white">
                        <table class="table table-bordered table-sm">
                          <tbody>
                            {locationSuggestions?.audience_geo?.result?.locations?.map(
                              (item, index) => (
                                <>
                                  <tr
                                    key={index}
                                    style={{
                                      cursor: "pointer",
                                      fontSize: "9px",
                                    }}
                                    onClick={() =>
                                      handleAddLocation(item, "audience_geo")
                                    }
                                  >
                                    <td> {item.name}</td>
                                    <td> {item.title}</td>
                                  </tr>
                                </>
                              ),
                            )}
                          </tbody>
                        </table>
                        {/* <div
                              key={index}
                              className="p-2"
                              style={{ cursor: "pointer" }}
                              onClick={() =>
                                handleAddLocation(item, "audience_geo")
                              }
                            >
                              {item.name}
                            </div> */}
                      </div>
                    )}

                    {locationSuggestions?.geo?.result?.locations?.length >
                      0 && (
                      <div className="border mt-1 bg-white">
                        <table class="table table-bordered table-sm">
                          <tbody>
                            {locationSuggestions?.geo?.result?.locations?.map(
                              (item, index) => (
                                <>
                                  <tr
                                    key={index}
                                    style={{
                                      cursor: "pointer",
                                      fontSize: "9px",
                                    }}
                                    onClick={() =>
                                      handleAddLocation(item, "geo")
                                    }
                                  >
                                    <td> {item.name}</td>
                                    <td> {item.title}</td>
                                  </tr>
                                </>
                                // <div
                                //   key={index}
                                //   className="p-2"
                                //   style={{ cursor: "pointer" }}
                                //   onClick={() => handleAddLocation(item, "geo")}
                                // >
                                //   {item.name}
                                // </div>
                              ),
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-sm-3 col-md-3 col-lg-3 col-6 dropdown">
                <button
                  className="btn filter-btn d-flex align-items-center gap-2"
                  data-bs-toggle="dropdown"
                  data-bs-auto-close="outside"
                  aria-expanded="false"
                >
                  <i className="ri-translate-2"></i>
                  Language
                  <i className="ri-arrow-down-s-line"></i>
                </button>

                <div className="dropdown-menu p-4 shadow filter-panel">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label d-flex align-items-center gap-2">
                        <i className="ri-group-line"></i> Audience
                        <i className="ri-information-fill text-muted"></i>
                      </label>
                      <input
                        className="form-control"
                        placeholder="Language"
                        value={languageInput.audience_lang}
                        onChange={(e) =>
                          handleLanguageSearch(e.target.value, "audience_lang")
                        }
                      />
                      {languageSuggestions.audience_lang.length > 0 && (
                        <div className="border mt-1 bg-white">
                          {languageSuggestions.audience_lang.map((item) => (
                            <div
                              key={item.id}
                              className="p-2"
                              style={{ cursor: "pointer" }}
                              onClick={() =>
                                handleAddLanguage(item, "audience_lang")
                              }
                            >
                              {item.name}
                            </div>
                          ))}
                        </div>
                      )}
                      {formData.filter.audience_lang.map((lang, index) => (
                        <span key={index} className="badge bg-primary me-2">
                          {lang.name}
                          <span
                            style={{ cursor: "pointer", marginLeft: "5px" }}
                            onClick={() =>
                              handleRemoveLanguage(lang.id, "audience_lang")
                            }
                          >
                            ❌
                          </span>
                        </span>
                      ))}
                    </div>

                    <div className="col-md-6">
                      <label className="form-label d-flex align-items-center gap-2">
                        <i className="ri-megaphone-line"></i> Influencer
                        <i className="ri-information-fill text-muted"></i>
                      </label>
                      <input
                        className="form-control"
                        placeholder="Language"
                        value={languageInput.lang}
                        onChange={(e) =>
                          handleLanguageSearch(e.target.value, "lang")
                        }
                      />
                      {languageSuggestions.lang.length > 0 && (
                        <div className="border mt-1 bg-white">
                          {languageSuggestions.lang.map((item) => (
                            <div
                              key={item.id}
                              className="p-2"
                              style={{ cursor: "pointer" }}
                              onClick={() => handleAddLanguage(item, "lang")}
                            >
                              {item.name}
                            </div>
                          ))}
                        </div>
                      )}
                      {formData.filter.lang.map((lang, index) => (
                        <span key={index} className="badge  bg-success me-2">
                          {lang.name}
                          <span
                            style={{ cursor: "pointer", marginLeft: "5px" }}
                            onClick={() =>
                              handleRemoveLanguage(lang.id, "lang")
                            }
                          >
                            ❌
                          </span>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-sm-3 col-md-3 col-lg-3 col-6 dropdown">
                <button
                  className="btn filter-btn d-flex align-items-center gap-2"
                  data-bs-toggle="dropdown"
                  data-bs-auto-close="outside"
                  aria-expanded="false"
                >
                  <i className="ri-user-6-fill"></i>
                  Gender
                  <i className="ri-arrow-down-s-line"></i>
                </button>

                <div className="dropdown-menu p-4 shadow filter-panel">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label d-flex align-items-center gap-2">
                        <i className="ri-group-line"></i> Followers
                        <i className="ri-information-fill text-muted"></i>
                      </label>
                      <div className="form-check mb-2">
                        <input
                          type="radio"
                          name="filter.audience_gender.code"
                          value="MALE"
                          className="form-check-input"
                          checked={
                            formData.filter.audience_gender.code === "MALE"
                          }
                          onChange={handleChange}
                        />
                        <label className="form-check-label" for="radioDefault2">
                          Male
                        </label>
                      </div>
                      <div className="form-check mb-2">
                        <input
                          type="radio"
                          name="filter.audience_gender.code"
                          value="FEMALE"
                          className="form-check-input"
                          checked={
                            formData.filter.audience_gender.code === "FEMALE"
                          }
                          onChange={handleChange}
                        />
                        <label className="form-check-label" for="radioDefault3">
                          Female
                        </label>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label d-flex align-items-center gap-2">
                        <i className="ri-megaphone-line"></i> Influencer
                        <i className="ri-information-fill text-muted"></i>
                      </label>
                      <div className="form-check mb-2">
                        <input
                          type="radio"
                          name="filter.gender.code"
                          className="form-check-input"
                          value="MALE"
                          checked={formData.filter.gender.code === "MALE"}
                          onChange={handleChange}
                        />
                        <label className="form-check-label" for="radioDefault5">
                          Male
                        </label>
                      </div>
                      <div className="form-check mb-2">
                        <input
                          type="radio"
                          name="filter.gender.code"
                          className="form-check-input"
                          value="FEMALE"
                          checked={formData.filter.gender.code === "FEMALE"}
                          onChange={handleChange}
                        />
                        <label className="form-check-label" for="radioDefault6">
                          Female
                        </label>
                      </div>
                      <div className="form-check mb-2">
                        <input
                          type="radio"
                          name="filter.gender.code"
                          className="form-check-input"
                          value="KNOWN"
                          checked={formData.filter.gender.code === "KNOWN"}
                          onChange={handleChange}
                        />
                        <label className="form-check-label" for="radioDefault7">
                          Any
                        </label>
                      </div>
                      <div className="form-check mb-2">
                        <input
                          type="radio"
                          className="form-check-input"
                          name="filter.gender.code"
                          value="UNKNOWN"
                          checked={formData.filter.gender.code === "UNKNOWN"}
                          onChange={handleChange}
                        />
                        <label className="form-check-label" for="radioDefault8">
                          Business Accounts
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-sm-3 col-md-3 col-lg-3 col-6 dropdown">
                <button
                  className="btn filter-btn d-flex align-items-center gap-2"
                  data-bs-toggle="dropdown"
                  data-bs-auto-close="outside"
                  aria-expanded="false"
                >
                  <i className="ri-user-follow-fill"></i>
                  Age
                  <i className="ri-arrow-down-s-line"></i>
                </button>

                <div
                  className="dropdown-menu p-4 shadow filter-panel"
                  style={{ minWidth: "320p" }}
                >
                  <div className="row g-4">
                    <div className="col-12">
                      <label className="form-label d-flex align-items-center gap-2 mb-2">
                        <i className="ri-group-line"></i>
                        Audience Age
                        <i className="ri-information-fill text-muted ms-auto"></i>
                      </label>
                      <div className="row g-2">
                        <select
                          className="form-select"
                          name="filter.audience_age"
                          value={formData?.filter?.audience_age || ""}
                          onChange={handleChange}
                        >
                          <option value="">Select Age</option>
                          <option value="13-17">13-17 year</option>
                          <option value="18-24">18-24 year</option>
                          <option value="25-34">25-34 year</option>
                          <option value="35-44">35-44 year</option>
                          <option value="45-64">45-64 year</option>
                          <option value="65-">65+ year</option>
                        </select>
                      </div>
                    </div>

                    <div className="col-12">
                      <label className="form-label d-flex align-items-center gap-2 mb-2">
                        <i className="ri-megaphone-line"></i>
                        Influencer Age
                        <i className="ri-information-fill text-muted ms-auto"></i>
                      </label>

                      <div className="row g-2">
                        <div className="col-6">
                          <select
                            className="form-select"
                            name="filter.age.left_number"
                            value={formData.filter.age.left_number || ""}
                            onChange={handleChange}
                          >
                            <option value="">Select Age</option>
                            <option value={18}>18</option>
                            <option value={25}>25</option>
                            <option value={35}>35</option>
                            <option value={45}>45</option>
                            <option value={65}>65</option>
                          </select>
                        </div>

                        <div className="col-6">
                          <select
                            className="form-select"
                            name="filter.age.right_number"
                            value={formData.filter.age.right_number || ""}
                            onChange={handleChange}
                          >
                            <option value="">Select Age</option>
                            <option value={18}>18</option>
                            <option value={25}>25</option>
                            <option value={35}>35</option>
                            <option value={45}>45</option>
                            <option value={65}>65</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-sm-3 col-md-3 col-lg-3 col-6 dropdown">
                <button
                  className="btn filter-btn d-flex align-items-center gap-2"
                  data-bs-toggle="dropdown"
                  data-bs-auto-close="outside"
                  aria-expanded="false"
                >
                  <i className="ri-menu-2-fill"></i>
                  Bio
                  <i className="ri-arrow-down-s-line"></i>
                </button>

                <div
                  className="dropdown-menu p-4 shadow filter-panel"
                  style={{ minWidth: "320px" }}
                >
                  <div className="row g-4">
                    <div className="col-12">
                      {/* <input type="text" className="form-control" placeholder="Keywords in Bio" /> */}
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Keywords in Bio"
                        value={formData.filter.text}
                        name="filter.text"
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-sm-3 col-md-3 col-lg-3 col-6 dropdown">
                <button
                  className="btn filter-btn d-flex align-items-center gap-2"
                  data-bs-toggle="dropdown"
                  data-bs-auto-close="outside"
                  aria-expanded="false"
                >
                  <i className="ri-user-voice-fill"></i>
                  Followers
                  <i className="ri-arrow-down-s-line"></i>
                </button>

                <div
                  className="dropdown-menu p-4 shadow filter-panel"
                  style={{ minWidth: "320px" }}
                >
                  <div className="row g-4">
                    <div className="col-12">
                      <label className="form-label text-muted small mb-1">
                        Min Followers
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Min Followers count"
                        name="filter.followers.left_number"
                        value={formData.filter.followers.left_number}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label text-muted small mb-1">
                        Max Followers
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Max Followers count"
                        name="filter.followers.right_number"
                        value={formData.filter.followers.right_number}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-sm-3 col-md-3 col-lg-3 col-6 dropdown">
                <button
                  className="btn filter-btn d-flex align-items-center gap-2"
                  data-bs-toggle="dropdown"
                  data-bs-auto-close="outside"
                  aria-expanded="false"
                >
                  <i className="ri-mouse-fill"></i>
                  Engagements
                  <i className="ri-arrow-down-s-line"></i>
                </button>

                <div
                  className="dropdown-menu p-4 shadow filter-panel"
                  style={{ minWidth: "320px" }}
                >
                  <div className="row g-4">
                    <div className="col-12">
                      <label className="form-label text-muted small mb-1">
                        Min Engagements
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Min Engagements"
                        name="filter.engagements.left_number"
                        value={formData.filter.followers.left_number}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label text-muted small mb-1">
                        Max Engagements
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Max Engagements"
                        name="filter.engagements.right_number"
                        value={formData.filter.followers.right_number}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>
              </div>
              {/* <div className="col-sm-3 col-md-3 col-lg-3 col-6 dropdown">
                <button
                  className="btn filter-btn d-flex align-items-center gap-2"
                  data-bs-toggle="dropdown"
                  data-bs-auto-close="outside"
                  aria-expanded="false"
                >
                  <i className="ri-play-circle-fill"></i>
                  Reel Plays
                  <i className="ri-arrow-down-s-line"></i>
                </button>

                <div
                  className="dropdown-menu p-4 shadow filter-panel"
                  style={{ minWidth: "320px" }}
                >
                  <div className="row g-4">
                    <div className="col-12">
                      <label className="form-label text-muted small mb-1">
                        Min Views
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Min Views"
                        name="filter.reels_plays.left_number"
                        onChange={handleChange}
                      />
                    </div>

                    <div className="col-12">
                      <label className="form-label text-muted small mb-1">
                        Max Views
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Max Views"
                        name="filter.reels_plays.right_number"
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>
              </div> */}
              <div className="col-sm-3 col-md-3 col-lg-3 col-6 dropdown">
                <button
                  className="btn filter-btn d-flex align-items-center gap-2"
                  data-bs-toggle="dropdown"
                  data-bs-auto-close="outside"
                  aria-expanded="false"
                >
                  <i className="ri-play-circle-fill"></i>
                  {title}
                  <i className="ri-arrow-down-s-line"></i>
                </button>

                <div
                  className="dropdown-menu p-4 shadow filter-panel"
                  style={{ minWidth: "320px" }}
                >
                  <div className="row g-4">
                    <div className="col-12">
                      <label className="form-label text-muted small mb-1">
                        Min Views
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Min Views"
                        name={`${fieldName}.left_number`}
                        value={
                          isVideoPlatform
                            ? formData.filter.views.left_number || ""
                            : formData.filter.reels_plays.left_number || ""
                        }
                        onChange={handleChange}
                      />
                    </div>

                    <div className="col-12">
                      <label className="form-label text-muted small mb-1">
                        Max Views
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Max Views"
                        name={`${fieldName}.right_number`}
                        value={
                          isVideoPlatform
                            ? formData.filter.views.right_number || ""
                            : formData.filter.reels_plays.right_number || ""
                        }
                        
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-sm-3 col-md-3 col-lg-3 col-6 dropdown">
                <button
                  className="btn filter-btn d-flex align-items-center gap-2"
                  data-bs-toggle="dropdown"
                  data-bs-auto-close="outside"
                >
                  <i className="ri-phone-fill"></i>
                  Contacts
                  <i className="ri-arrow-down-s-line"></i>
                </button>

                <div
                  className="dropdown-menu p-4 shadow filter-panel"
                  style={{ minWidth: "320px" }}
                >
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label d-flex align-items-center gap-2 mb-2">
                        <i className="ri-group-line"></i>
                        Contacts
                        <i className="ri-information-fill text-muted ms-auto"></i>
                      </label>

                      <input
                        type="text"
                        className="form-control mb-3"
                        placeholder="Select Contacts"
                        // value={formData.filter.contacts.join(", ")}
                        value={(formData?.filter?.with_contact || []).join(
                          ", ",
                        )}
                        readOnly
                        onClick={() => setShowContacts(!showContacts)}
                      />
                    </div>

                    {showContacts && (
                      <ul className="list-group" style={{ marginTop: "-11px" }}>
                        {contactOptions.map((option) => (
                          <li
                            key={option.value}
                            className={`list-group-item list-group-item-action ${
                              (formData?.filter?.with_contact || []).includes(
                                option.value,
                              )
                                ? "active"
                                : ""
                            }`}
                            onClick={() => handleContactSelect(option)}
                            style={{ cursor: "pointer" }}
                          >
                            {option.label}
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Selected Chips */}
                    <div className="mt-3 d-flex flex-wrap gap-2">
                      {/* {formData.filter.contacts.map(contact => ( */}
                      {(formData?.filter?.with_contact || []).map((contact) => (
                        <span
                          key={contact}
                          className="badge bg-primary d-flex align-items-center gap-2"
                        >
                          {contact}
                          <i
                            className="ri-close-line"
                            style={{ cursor: "pointer" }}
                            onClick={() => handleContactSelect(contact)}
                          />
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-sm-3 col-md-3 col-lg-3 col-6 dropdown">
                <button
                  className="btn filter-btn d-flex align-items-center gap-2"
                  data-bs-toggle="dropdown"
                  data-bs-auto-close="outside"
                  aria-expanded="false"
                >
                  <i className="ri-calendar-todo-fill"></i>
                  Last Posted
                  <i className="ri-arrow-down-s-line"></i>
                </button>
                <div className="dropdown-menu p-4 shadow filter-panel">
                  <div className="row g-3">
                    <div className="col-md-12">
                      <label className="form-label d-flex align-items-center gap-2">
                        <i className="ri-megaphone-line"></i> Influencer
                        <i className="ri-information-fill text-muted"></i>
                      </label>
                      <div className="form-check mb-2">
                        <input
                         className="form-check-input me-2"
                          type="radio"
                          name="filter.last_posted"
                          value={30}
                          onChange={handleChange}
                        />

                        <label className="form-check-label" for="radioDefault4">
                          1 Month
                        </label>
                      </div>
                      <div className="form-check mb-2">
                        <input
                         className="form-check-input me-2"
                          type="radio"
                          name="filter.last_posted"
                          value={90}
                          onChange={handleChange}
                        />

                        <label className="form-check-label" for="radioDefault5">
                          3 Months
                        </label>
                      </div>
                      <div className="form-check mb-2">
                        <input
                         className="form-check-input me-2"
                          type="radio"
                          name="filter.last_posted"
                          value={180}
                          onChange={handleChange}
                        />

                        <label className="form-check-label" for="radioDefault6">
                          6 Months
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

             {channelName === "youtube" && <div className="col-sm-3 col-md-3 col-lg-3 col-6 dropdown">
                <button
                  className="btn filter-btn d-flex align-items-center gap-2"
                  data-bs-toggle="dropdown"
                  data-bs-auto-close="outside"
                  aria-expanded="false"
                >
                  <i className="ri-calendar-todo-fill"></i>
                  Post Type
                  <i className="ri-arrow-down-s-line"></i>
                </button>
                <div className="dropdown-menu p-4 shadow filter-panel">
                  <div className="row g-3">
                    <div className="col-md-12">
                      <label className="form-label d-flex align-items-center gap-2">
                        <i className="ri-megaphone-line"></i> Influencer
                        <i className="ri-information-fill text-muted"></i>
                      </label>
                      <div className="form-check mb-2">
                        <input
                         className="form-check-input me-2"
                          type="radio"
                          name="filter.last_posted"
                          value="all"
                          onChange={handleChange}
                        />

                        <label className="form-check-label" for="radioDefault4">
                           {" "} All Posts
                        </label>
                      </div>
                      <div className="form-check mb-2">
                        <input
                         className="form-check-input me-2"
                          type="radio"
                          name="filter.last_posted"
                          value="videos"
                          onChange={handleChange}
                        />

                        <label className="form-check-label" for="radioDefault5">
                          {" "} Videos
                        </label>
                      </div>
                      <div className="form-check mb-2">
                        <input
                         className="form-check-input me-2"
                          type="radio"
                          name="filter.last_posted"
                          value="shorts"
                          onChange={handleChange}
                        />

                        <label className="form-check-label" for="radioDefault6">
                          {" "} Shorts
                        </label>
                      </div>
                      <div className="form-check mb-2">
                        <input
                         className="form-check-input me-2"
                          type="radio"
                          name="filter.last_posted"
                          value="streams"
                          onChange={handleChange}
                        />

                        <label className="form-check-label" for="radioDefault6">
                          {" "} Streams
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>}

              {/* <div className="col-sm-3 col-md-3 col-lg-3 col-6 dropdown">
                <button
                  className="btn filter-btn d-flex align-items-center gap-2"
                  data-bs-toggle="dropdown"
                  data-bs-auto-close="outside"
                >
                  <i className="ri-hashtag"></i>
                  Text Tags
                  <i className="ri-arrow-down-s-line"></i>
                </button>

                <div
                  className="dropdown-menu p-4 shadow filter-panel"
                  style={{ minWidth: "320px" }}
                >
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label d-flex align-items-center gap-2 mb-2">
                        <i className="ri-group-line"></i>
                        Please use @ for mentions and # for hashtags before each
                        keyword.
                        <i className="ri-information-fill text-muted ms-auto"></i>
                      </label>

                      <input
                        type="text"
                        className="form-control"
                        placeholder="Tags"
                        name="filter.text_tags.include"
                        onChange={handleChange}
                      />
                    </div>

                    <div className="col-12">
                      <label className="form-label d-flex align-items-center gap-2 mb-2">
                        <i className="ri-group-line"></i>
                        Excluding these ...
                        <i className="ri-information-fill text-muted ms-auto"></i>
                      </label>

                      <input
                        type="text"
                        className="form-control"
                        placeholder="Tags"
                        name="filter.text_tags.exclude"
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>
              </div> */}

              {/* {channelName !== "tiktok" && (
                <div className="col-sm-3 col-md-3 col-lg-3 col-6 dropdown">
                  <button
                    className="btn filter-btn d-flex align-items-center gap-2"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="outside"
                    aria-expanded="false"
                  >
                    <i className="ri-user-shared-2-fill"></i>
                    Audience Credebility
                    <i className="ri-arrow-down-s-line"></i>
                  </button>
                  <div className="dropdown-menu p-4 shadow filter-panel">
                    <div className="row g-3">
                      <div className="col-md-12">
                        <label className="form-label d-flex align-items-center gap-2">
                          <i className="ri-megaphone-line"></i> Select a value
                          <i className="ri-information-fill text-muted"></i>
                        </label>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            name="filter.audience_credibility_class"
                            value="low"
                            className="form-check-input"
                            onChange={handleChange}
                          />
                          <label
                            className="form-check-label"
                            for="checkDefault"
                          >
                            Low
                          </label>
                        </div>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            name="filter.audience_credibility_class"
                            value="hight"
                            className="form-check-input"
                            onChange={handleChange}
                          />
                          <label
                            className="form-check-label"
                            for="checkChecked"
                          >
                            High and Best
                          </label>
                        </div>

                        <select
                          className="form-select mt-3"
                          aria-label="Default select example"
                          name="filter.audience_credibility"
                          onChange={handleChange}
                        >
                          <option selected>Percentage</option>
                          <option value={1}>1%</option>
                          <option value={5}>5%</option>
                          <option value={10}>10%</option>
                          <option value={15}>15%</option>
                          <option value={20}>20%</option>
                          <option value={25}>25%</option>
                          <option value={30}>30%</option>
                          <option value={35}>35%</option>
                          <option value={40}>40%</option>
                          <option value={45}>45%</option>
                          <option value={50}>50%</option>
                          <option value={55}>55%</option>
                          <option value={60}>60%</option>
                          <option value={65}>65%</option>
                          <option value={70}>70%</option>
                          <option value={75}>75%</option>
                          <option value={80}>80%</option>
                          <option value={85}>85%</option>
                          <option value={90}>90%</option>
                          <option value={95}>95%</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )} */}
              <div className="col-sm-3 col-md-3 col-lg-3 col-6 dropdown">
                <button
                  className="btn filter-btn d-flex align-items-center gap-2"
                  data-bs-toggle="dropdown"
                  data-bs-auto-close="outside"
                  aria-expanded="false"
                >
                  <i className="ri-user-voice-fill"></i>
                  posts_count
                  <i className="ri-arrow-down-s-line"></i>
                </button>

                <div
                  className="dropdown-menu p-4 shadow filter-panel"
                  style={{ minWidth: "320px" }}
                >
                  <div className="row g-4">
                    <div className="col-12">
                      <label className="form-label text-muted small mb-1">
                        Min Followers
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Post count"
                        name="filter.posts_count.left_number"
                        value={formData.filter.posts_count.left_number}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label text-muted small mb-1">
                        Max Followers
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Post count"
                        name="filter.posts_count.right_number"
                        value={formData.filter.posts_count.right_number}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>
              </div>
              {!isVideoPlatform && (
                <div className="col-sm-3 col-md-3 col-lg-3 col-6 dropdown">
                  <button
                    className="btn filter-btn d-flex align-items-center gap-2"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="outside"
                    aria-expanded="false"
                  >
                    <i className="ri-user-shared-2-fill"></i>
                    Account Type
                    <i className="ri-arrow-down-s-line"></i>
                  </button>
                  <div className="dropdown-menu px-4 py-2 shadow filter-panel">
                    <div className="row g-3">
                      <div className="col-md-12">
                        <div className="form-check">
                          <input
                            type="checkbox"
                            name="filter.account_type"
                            value="1"
                            className="form-check-input"
                            onChange={handleChange}
                          />
                          {/* <input className="form-check-input" type="checkbox" value="" id="checkDefault1" /> */}
                          <label
                            className="form-check-label"
                            for="checkDefault"
                          >
                            Regular
                          </label>
                        </div>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            name="filter.account_type"
                            value="2"
                            className="form-check-input"
                            onChange={handleChange}
                          />
                          {/* <input className="form-check-input" type="checkbox" value="" id="checkChecked" /> */}
                          <label
                            className="form-check-label"
                            for="checkChecked"
                          >
                            Business
                          </label>
                        </div>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            name="filter.account_type"
                            value="3"
                            className="form-check-input"
                            onChange={handleChange}
                          />
                          {/* <input className="form-check-input" type="checkbox" value="" id="checkChecked" /> */}
                          <label
                            className="form-check-label"
                            for="checkChecked"
                          >
                            Influencer
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="d-flex justify-content-end align-items-end mt-4">
                <button
                  className="btn btn-light px-4 py-2 d-flex align-items-center gap-2  me-2"
                  onClick={handleClearFilter}
                >
                  <i className="ri-refresh-line me-1"></i>
                  Clear Filter
                </button>

                <button
                  className="btn btn-danger px-4 py-2 d-flex align-items-center gap-2"
                  onClick={handleSearch}
                >
                  <i className="ri-search-line"></i>
                  SEARCH
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {currentRows.length > 0 && (
        <div className="row">
          <div className="col-lg-12">
            <div className="card" id="leadsList">
              <div className="card-header border-0">
                <div className="row g-4 align-items-center">
                  <div className="col-sm-4">
                    <h4>Results Found </h4>
                  </div>
                  <div className="col-sm-auto ms-auto">
                    <div className="hstack gap-2">
                      <a href="#" className="btn btn-success add-btn">
                        <i className="ri-add-line align-bottom me-1"></i> Export
                        Results
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  {/* LEFT SIDE */}
                  <div className="d-flex align-items-center gap-2">
                    <span>Show</span>
                    <select
                      className="form-select form-select-sm"
                      style={{ width: "80px" }}
                      value={entriesPerPage}
                      onChange={(e) => {
                        const value = Number(e.target.value);

                        setEntriesPerPage(value);
                        setCurrentPage(1); // reset page

                        setFormData((prev) => ({
                          ...prev,
                          paging: {
                            ...prev.paging,
                            limit: value,
                            skip: 0,
                          },
                        }));
                      }}
                    >
                      <option value={15}>15</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>

                    <span>entries</span>
                  </div>

                  {/* RIGHT SIDE */}
                  <div className="d-flex align-items-center gap-2">
                    <label className="mb-0">Search:</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      style={{ width: "200px" }}
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                  </div>
                </div>
                <div className="table-responsive">
                  <table className="table table-bordered align-middle">
                    <thead>
                      <tr>
                        <th>
                          <input
                            type="checkbox"
                            checked={
                              currentRows.length > 0 &&
                              currentRows.every((r) =>
                                selectedRows.includes(r.id),
                              )
                            }
                            onChange={(e) => handleSelectAll(e.target.checked)}
                          />
                        </th>
                        <th>SR No.</th>
                        <th>Accounts</th>
                        <th>Followers</th>
                        <th>Engagement Rate</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentRows.length > 0 ? (
                        currentRows.map((row, index) => (
                          <tr key={row.id}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedRows.includes(row.id)}
                                onChange={() => handleRowSelect(row.id)}
                              />
                            </td>

                            <td>
                              {(currentPage - 1) * entriesPerPage + index + 1}
                            </td>

                            <td>
                              <div className="d-flex align-items-center gap-3">
                                <img
                                  src={row.image}
                                  width="48"
                                  height="48"
                                  className="rounded-circle"
                                  style={{ objectFit: "cover" }}
                                />
                                <div>
                                  <strong>{row.username}</strong>
                                  <div className="text-muted">{row.name}</div>
                                </div>
                              </div>
                            </td>

                            <td>{parseNumberValue(row.followers)}</td>

                            <td>{row.engagement?.toFixed(2)}%</td>

                            <td>
                              <label
                                className={`btn ${
                                  row.verified
                                    ? "btn-success-light"
                                    : "btn-warning-light"
                                }`}
                              >
                                {row.verified ? "Verified" : "Unverified"}
                              </label>
                            </td>

                            <td>
                              <div className="d-flex gap-2">
                                <button
                                  className="btn btn-success btn-sm"
                                  onClick={() => handleView(row)}
                                >
                                  <i className="ri-arrow-right-up-line me-1"></i>{" "}
                                  VIEW
                                </button>

                                <a
                                  href={row.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-primary btn-sm"
                                >
                                  Open
                                </a>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7" className="text-center py-4">
                            No Data Found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="row align-items-center mt-3">
                  <div className="col-sm-6">
                    {/* Showing{" "}
                    <strong>
                      {totalItems === 0 ? 0 : indexOfFirstItem + 1}
                    </strong>{" "}
                    to <strong>{Math.min(indexOfLastItem, totalItems)}</strong>{" "}
                    of <strong>{toatalItemRecord}</strong> entries */}
                    Showing <strong>{start}</strong> to <strong>{end}</strong>{" "}
                    of <strong>{totalItems}</strong>
                  </div>

                  <div className="col-sm-6">
                    <ul className="pagination justify-content-end mb-0">
                      <li
                        className={`page-item ${
                          currentPage === 1 ? "disabled" : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() =>
                            setCurrentPage((p) => Math.max(p - 1, 1))
                          }
                        >
                          Previous
                        </button>
                      </li>
                      <li
                        className={`page-item ${
                          currentPage === totalPages ? "disabled" : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() =>
                            setCurrentPage((p) => Math.min(p + 1, totalPages))
                          }
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <InsiteModal
        showRemarksModal={showRemarksModal}
        setShowRemarksModal={setShowRemarksModal}
        item={selectedRow}
      />
    </div>
  );
};

export default InfluencerDiscover;
